import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, 
  MapPin,
  Clock,
  Filter,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useWebSocketContext } from '../hooks/useWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ['All', 'scheduled', 'live', 'completed'];

export const Games = () => {
  const [games, setGames] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tournamentFilter, setTournamentFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  
  const { isConnected, onGameUpdate } = useWebSocketContext();

  useEffect(() => {
    fetchData();
  }, []);

  // Listen for real-time game updates
  useEffect(() => {
    const unsubscribe = onGameUpdate((message) => {
      if (message.update_type === 'score_update') {
        const { game_id, team1_score, team2_score, status, winner_id } = message.data;
        
        setGames(prevGames => 
          prevGames.map(game => 
            game.id === game_id 
              ? { ...game, team1_score, team2_score, status, winner_id }
              : game
          )
        );
        
        toast.info(`Score Update: Game ${game_id.slice(0, 8)}...`, {
          description: `${team1_score} - ${team2_score}`
        });
      }
    });
    
    return unsubscribe;
  }, [onGameUpdate]);

  const fetchData = async () => {
    try {
      const [gamesRes, tournamentsRes] = await Promise.all([
        axios.get(`${API}/games`),
        axios.get(`${API}/tournaments`)
      ]);
      setGames(gamesRes.data);
      setTournaments(tournamentsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGames = games.filter(g => {
    const matchesTournament = tournamentFilter === 'All' || g.tournament_id === tournamentFilter;
    const matchesStatus = statusFilter === 'All' || g.status === statusFilter;
    return matchesTournament && matchesStatus;
  });

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'live': return 'bg-green-500/10 text-green-500 border-green-500/20 tracing-beam';
      case 'completed': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default: return '';
    }
  };

  // Group games by status
  const liveGames = filteredGames.filter(g => g.status === 'live');
  const scheduledGames = filteredGames.filter(g => g.status === 'scheduled');
  const completedGames = filteredGames.filter(g => g.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const GameCard = ({ game, index }) => (
    <Card 
      className={`card-hover ${game.status === 'live' ? 'border-green-500' : ''}`}
      data-testid={`game-card-${index}`}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Badge variant="outline" className={getStatusColor(game.status)}>
            {game.status === 'live' ? '🔴 LIVE' : game.status.toUpperCase()}
          </Badge>
          <Badge variant="secondary">{game.round}</Badge>
        </div>
        
        {/* Teams & Score */}
        <div className="space-y-3">
          <div className={`flex items-center justify-between p-3 rounded-lg ${game.winner_id === game.team1_id ? 'bg-green-500/10' : 'bg-muted/50'}`}>
            <span className={`font-semibold ${game.winner_id === game.team1_id ? 'text-green-500' : ''}`}>
              {game.team1_name}
            </span>
            <span className="text-2xl font-mono font-bold">{game.team1_score}</span>
          </div>
          
          <div className="text-center text-muted-foreground text-sm">VS</div>
          
          <div className={`flex items-center justify-between p-3 rounded-lg ${game.winner_id === game.team2_id ? 'bg-green-500/10' : 'bg-muted/50'}`}>
            <span className={`font-semibold ${game.winner_id === game.team2_id ? 'text-green-500' : ''}`}>
              {game.team2_name}
            </span>
            <span className="text-2xl font-mono font-bold">{game.team2_score}</span>
          </div>
        </div>
        
        {/* Meta Info */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>{game.field}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{formatDateTime(game.scheduled_time)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="games-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">GAMES</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            View schedules, live games, and results
          </p>
        </div>
        <div className="flex items-center gap-2 mt-2 md:mt-0">
          {isConnected ? (
            <Badge variant="outline" className="text-green-500 border-green-500">
              <Wifi className="w-3 h-3 mr-1" />
              Live Updates
            </Badge>
          ) : (
            <Badge variant="outline" className="text-gray-500">
              <WifiOff className="w-3 h-3 mr-1" />
              Offline
            </Badge>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-8" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Filter className="w-4 h-4" />
              <span>Filters:</span>
            </div>
            
            <Select value={tournamentFilter} onValueChange={setTournamentFilter}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="tournament-filter">
                <SelectValue placeholder="Tournament" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Tournaments</SelectItem>
                {tournaments.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s === 'All' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Live Games Section */}
      {liveGames.length > 0 && (
        <div className="mb-8" data-testid="live-games-section">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            Live Now
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {liveGames.map((game, index) => (
              <GameCard key={game.id} game={game} index={`live-${index}`} />
            ))}
          </div>
        </div>
      )}

      {/* Scheduled Games */}
      {scheduledGames.length > 0 && (
        <div className="mb-8" data-testid="scheduled-games-section">
          <h2 className="text-2xl font-bold mb-4">Upcoming Games</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scheduledGames.map((game, index) => (
              <GameCard key={game.id} game={game} index={`scheduled-${index}`} />
            ))}
          </div>
        </div>
      )}

      {/* Completed Games */}
      {completedGames.length > 0 && (
        <div data-testid="completed-games-section">
          <h2 className="text-2xl font-bold mb-4">Completed</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {completedGames.map((game, index) => (
              <GameCard key={game.id} game={game} index={`completed-${index}`} />
            ))}
          </div>
        </div>
      )}

      {/* No Games */}
      {filteredGames.length === 0 && (
        <Card className="text-center py-12" data-testid="no-games">
          <CardContent>
            <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Games Found</h3>
            <p className="text-muted-foreground">
              {tournamentFilter !== 'All' || statusFilter !== 'All'
                ? 'Try adjusting your filters'
                : 'No games have been scheduled yet'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
